import React, { useId, useState, useRef, useEffect } from 'react';

import { CFColorTokenProp } from '../../../theme/tokens/contentful-color';
import {
  WidgetProps,
  DEFAULT_BUTTON_TEXT,
  SharedProps,
  DEFAULT_EMAIL_LABEL
} from '../shared';
import cx from 'classnames';

import { MobilePhoneNumberInput } from './MobilePhoneNumberInput';
import { MobilePhoneNumberButton } from './MobilePhoneNumberButton';
import { usePageDataContext } from '~/context/PageData';
import { BannerMessage } from '../BannerMessage';
import RichText from '~/components/RichText';

import { RenderNode } from '@contentful/rich-text-react-renderer';
import { INLINES } from '@contentful/rich-text-types';
import { Block, BLOCKS, Inline } from '@contentful/rich-text-types';
import { FloatLabel } from '~/components/Input/FloatLabel';

interface MobilePhoneNumberWidgetProps extends WidgetProps, SharedProps {
  useDropshadow?: boolean;
  responsive?: boolean;
}

const ctaOverrideOptions: RenderNode = {
  [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
    return (
      <p className="w-fit items-center justify-center text-[10px] leading-4">
        {children}
      </p>
    );
  },
  [INLINES.HYPERLINK]: (node: any) => {
    return (
      <a
        href={node.data.uri}
        target={node.data.target}
        rel={node.data.target === '_blank' ? 'noopener noreferrer' : undefined}
        className="text-blue-600 focus:ring-blue-500 underline focus:outline-none focus:ring-2"
      >
        {node.content[0].value}
      </a>
    );
  }
};

export function MobilePhoneNumberWidget({
  autocompleteAttribute,
  buttonColor = 'ginger',
  buttonHoverColor = 'green',
  borderColor = '#3BBFAD',
  buttonText = DEFAULT_BUTTON_TEXT,
  returningUserButtonText,
  buttonContent,
  label = autocompleteAttribute?.includes('email')
    ? DEFAULT_EMAIL_LABEL
    : undefined,
  privacyDisclaimerDropdown,
  errorMessage,
  errorPosition,
  isReturning,
  showErrorBanner,
  inputProps,
  segmentEventName,
  submitted,
  successMessage = 'Thank You!',
  useDropshadow = false,
  responsive
}: MobilePhoneNumberWidgetProps) {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(false);
  const [shouldFocusDisclaimer, setShouldFocusDisclaimer] =
    useState<boolean>(false);
  const generatedId = useId();
  const errorId = useId();
  const disclaimerId = useId();
  const inputId = inputProps?.id
    ? `${inputProps.id}-${generatedId}`
    : generatedId;
  const errorRef = useRef<HTMLDivElement>(null);
  const disclaimerRef = useRef<HTMLDivElement>(null);

  // Focus management: Move focus to input when error appears
  useEffect(() => {
    if (errorMessage) {
      setTimeout(() => {
        const input = document.getElementById(inputId) as HTMLInputElement;
        if (input) {
          input.focus();
        }
      }, 200);
    }
  }, [errorMessage, inputId]);

  const { cookies: { userCTAInteraction } = {} } = usePageDataContext();
  const interactedWithCTA =
    userCTAInteraction && userCTAInteraction?.includes(segmentEventName || '');

  const { value } = inputProps ?? {};

  // Shows disclaimer when input receives focus
  const handleInputFocus = () => {
    setIsFocused(true);
    if (privacyDisclaimerDropdown) {
      setShowDisclaimer(true);
    }
  };

  // Hides disclaimer only when focus leaves the entire widget
  const handleInputBlur = (e: React.FocusEvent) => {
    const relatedTarget = e.relatedTarget as Element;
    const widgetContainer = relatedTarget?.closest(
      '[data-testid="mobile-phone-widget"]'
    );
    if (!widgetContainer) {
      setIsFocused(false);
      setShowDisclaimer(false);
    }
  };

  // Shows disclaimer on Tab key press for keyboard accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && privacyDisclaimerDropdown && !showDisclaimer) {
      setShowDisclaimer(true);
      setShouldFocusDisclaimer(true);
    }
  };

  // Hides disclaimer when clicking outside input but within widget
  const handleWidgetClick = (e: React.MouseEvent) => {
    // Any click inside the widget should focus the input and show the disclaimer (if applicable)
    e.stopPropagation();
    setIsFocused(true);
    if (privacyDisclaimerDropdown) {
      setShowDisclaimer(true);
    }
  };

  // Handles keyboard navigation: Enter (form submit vs disclaimer), Space (button-like), Escape (close)
  const handleWidgetKeyDown = (e: React.KeyboardEvent) => {
    const target = e.target as Element;
    let isInput = target.id === inputId;
    if (!isInput && inputId) {
      let current: Element | null = target.parentElement;
      while (current && !isInput) {
        if (current.id === inputId) {
          isInput = true;
          break;
        }
        current = current.parentElement;
      }
    }

    if (e.key === 'Enter') {
      if (isInput) {
        return;
      } else {
        e.preventDefault();
        setIsFocused(false);
        setShowDisclaimer(false);
      }
    }

    if (e.key === ' ') {
      e.preventDefault();
      if (!isInput) {
        setIsFocused(false);
        setShowDisclaimer(false);
      }
    }

    if (e.key === 'Escape' && showDisclaimer) {
      setShowDisclaimer(false);
      setIsFocused(false);
      const input = document.getElementById(inputId) as HTMLInputElement;
      if (input) {
        input.focus();
      }
    }
  };

  // Moves focus to disclaimer when shown via keyboard navigation
  useEffect(() => {
    if (showDisclaimer && shouldFocusDisclaimer && disclaimerRef.current) {
      setTimeout(() => {
        disclaimerRef.current?.focus();
        setShouldFocusDisclaimer(false);
      }, 100);
    }
  }, [showDisclaimer, shouldFocusDisclaimer]);

  // Hides disclaimer when clicking anywhere outside the widget
  useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as Element;
      const widgetContainer = target.closest(
        '[data-testid="mobile-phone-widget"]'
      );

      if (!widgetContainer) {
        setIsFocused(false);
        setShowDisclaimer(false);
      }
    };

    if (showDisclaimer) {
      document.addEventListener('mousedown', handleGlobalClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleGlobalClick);
    };
  }, [showDisclaimer]);

  return (
    <div
      data-testid="mobile-phone-widget"
      onClick={handleWidgetClick}
      onKeyDown={handleWidgetKeyDown}
      role="button"
      tabIndex={0}
      className={cx(
        'relative z-[3] flex h-full max-h-16 flex-row items-center justify-center',
        {
          'w-fit': !responsive
        }
      )}
    >
      <div className="z-[1] flex h-full w-full items-center">
        <FloatLabel
          htmlFor={inputId}
          id={`${inputId}-label`}
          floatLabel={!!value || isFocused}
          submitted={submitted && !!segmentEventName}
          data-testid="mobile-phone-number-label"
        >
          {!submitted ? label : ''}
        </FloatLabel>
        <MobilePhoneNumberInput
          {...inputProps}
          type={autocompleteAttribute ?? 'tel'}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          className="font-medium"
          id={inputId}
          placeholder=""
          autoComplete={autocompleteAttribute ?? 'tel'}
          borderColor={
            showErrorBanner && errorMessage ? '#D31400' : borderColor
          }
          floatLabel={!!value || isFocused}
          submitted={submitted && !!segmentEventName}
          value={submitted && !!segmentEventName ? successMessage : value}
          data-e2e="phone-number"
          data-testid="mobile-phone-number-input"
          aria-invalid={!!errorMessage}
          aria-describedby={errorMessage ? errorId : undefined}
          useDropshadow={useDropshadow}
          isReturning={isReturning}
          responsive={responsive}
        />
        {!submitted && (
          <MobilePhoneNumberButton
            type="submit"
            cfButtonColor={buttonColor as CFColorTokenProp}
            cfButtonHoverColor={buttonHoverColor as CFColorTokenProp}
            useDropshadow={useDropshadow}
            data-e2e="btn-getting-started"
            data-testid="btn-getting-started"
          >
            {(!segmentEventName && isReturning) || interactedWithCTA
              ? returningUserButtonText ?? (buttonContent || buttonText)
              : buttonContent || buttonText}
          </MobilePhoneNumberButton>
        )}
      </div>
      {!!privacyDisclaimerDropdown && (
        <div
          data-testid="privacy-disclaimer"
          ref={disclaimerRef}
          id={disclaimerId}
          tabIndex={-1}
          role="region"
          aria-labelledby={`${inputId}-label`}
          aria-describedby={inputId}
          className={cx(
            'absolute left-0 top-full -mt-1 h-8 w-full overflow-hidden rounded-b-lg bg-[#F8F7F3] transition-all duration-300',
            {
              'max-h-14 opacity-100': showDisclaimer,
              'max-h-0 opacity-0': !showDisclaimer
            }
          )}
        >
          <div className="flex h-full items-center justify-center">
            <RichText
              data={privacyDisclaimerDropdown}
              overrideOptions={ctaOverrideOptions}
            />
          </div>
        </div>
      )}
      {showErrorBanner && errorMessage && (
        <BannerMessage
          ref={errorRef}
          id={errorId}
          message={errorMessage}
          position={errorPosition}
          isDisclaimerPresent={!!privacyDisclaimerDropdown}
          variant="error"
          role="alert"
          aria-live="polite"
          tabIndex={-1}
        />
      )}
      {submitted && (
        <BannerMessage
          message={successMessage}
          isDisclaimerPresent={!!privacyDisclaimerDropdown}
          position="bottom"
          variant="success"
        />
      )}
    </div>
  );
}
